// src/features/developers/components/RecipesTab.tsx
import React, { useState } from 'react';
import { useTranslate } from 'react-admin';
import { Box, Card, Typography, useTheme } from '@mui/material';
import type { RenderedRecipe } from '../recipes';
import { RECIPE_LANGUAGES, type RecipeLanguage } from '../types';
import { CodeBlock, InlineText, MONO } from './common';

interface RecipesTabProps {
  recipes: RenderedRecipe[];
  baseUrl: string;
  copiedId: string | null;
  onCopy: (id: string, text: string) => void;
}

export const RecipesTab: React.FC<RecipesTabProps> = ({ recipes, baseUrl, copiedId, onCopy }) => {
  const translate = useTranslate();
  const theme = useTheme();
  const [language, setLanguage] = useState<RecipeLanguage>('curl');
  const render = (code: string): string => code.split('{{baseUrl}}').join(baseUrl);

  return (
    <Box>
      <Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary, lineHeight: 1.65, maxWidth: 820 }}>
        <InlineText text={translate('developers.recipes.intro', { baseUrl })} />
      </Typography>
      <Box role="group" aria-label={translate('developers.recipes.language')} sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', my: 2 }}>
        {RECIPE_LANGUAGES.map((l) => {
          const selected = language === l.key;
          return (
            <Box
              key={l.key}
              component="button"
              type="button"
              aria-pressed={selected}
              onClick={() => setLanguage(l.key)}
              sx={{
                border: `1px solid ${selected ? theme.custom.ctaFill : theme.palette.divider}`,
                backgroundColor: selected ? theme.custom.ctaFill : theme.palette.background.paper,
                color: selected ? theme.palette.common.white : theme.palette.text.secondary,
                cursor: 'pointer',
                font: 'inherit',
                fontSize: '0.8125rem',
                fontWeight: 500,
                borderRadius: '9999px',
                px: 1.75,
                minHeight: 32,
                '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
              }}
            >
              {l.label}
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: 'grid', gap: 2 }}>
        {recipes.map((recipe) => (
          <Card key={recipe.key} sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <Typography component="h2" sx={{ fontSize: '1.0625rem', fontWeight: 600, m: 0 }}>
                {translate(`developers.recipes.${recipe.key}.title`)}
              </Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: theme.custom.text.tertiary, overflowWrap: 'anywhere' }}>{recipe.endpoint}</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary, lineHeight: 1.65, mt: 0.75, maxWidth: 860 }}>
              <InlineText text={translate(`developers.recipes.${recipe.key}.description`)} />
            </Typography>
            <CodeBlock id={`recipe-${recipe.key}`} code={render(recipe.code[language])} copiedId={copiedId} onCopy={onCopy} />
          </Card>
        ))}
      </Box>
    </Box>
  );
};
